import { getScales } from "@/components/PddContextProvider/DiceCanvases/helpers/getScales";
import { isDebug, PddPayloadTypes } from "@/constants";
import { Context } from "@/context";
import { isEmpty } from "lodash";
import { useContext, useEffect } from "react";

export const usePhysicsListeners = () => {
  const {
    userManifest,
    logger,
    errorCallback,
    toastCallback,
    physicsWorker,
    setHasPhysicsWorkerErrored,
    setIsPhysicsWorkerReady,
    renderedWorker,
  } = useContext(Context);

  useEffect(() => {
    if (isEmpty(userManifest) || !physicsWorker || !renderedWorker) return;

    if (isDebug) {
      console.log("Setting up physicsWorker listeners");
    }

    const handlePhysicsMessage = ({
      data: { type, payload },
    }: MessageEvent) => {
      if (type === PddPayloadTypes.ComponentMounted) {
        const scales = getScales(userManifest);

        if (!isEmpty(scales)) {
          physicsWorker.postMessage({
            type: PddPayloadTypes.Scales,
            payload: scales,
          });
        }

        setIsPhysicsWorkerReady?.(true);

        if (isDebug) {
          console.log("[physicsWorkerListeners] ready");
        }
      } else if (type === PddPayloadTypes.ToastMessage) {
        toastCallback?.(payload);
      } else if (type === PddPayloadTypes.RenderDice) {
        renderedWorker.postMessage({
          type: PddPayloadTypes.RenderDice,
          payload,
        });
      } else if (type === PddPayloadTypes.Scales) {
        const scales = getScales(payload);

        if (!isEmpty(scales)) {
          physicsWorker.postMessage({
            type: PddPayloadTypes.Scales,
            payload: scales,
          });
        }
      } else if (type === PddPayloadTypes.PreRoll) {
        if (userManifest[payload.data.setId]) {
          if (isDebug) {
            console.log(
              "[physicsWorkerListeners] setId is user's, starting roll",
            );
          }

          physicsWorker.postMessage({
            type: PddPayloadTypes.StartRoll,
            payload,
          });
        } else {
          if (isDebug) {
            console.warn(
              `[physicsWorkerListeners] Manifest not found for setId ${payload.data.setId}. running preRoll steps in renderedWorker before starting roll`,
            );
          }

          renderedWorker.postMessage({
            type: PddPayloadTypes.PreRoll,
            payload,
          });
        }
      }
    };
    const handleError = (error: ErrorEvent) => {
      // clean up message handler if error happens before component mounts
      physicsWorker.removeEventListener("message", handlePhysicsMessage);

      logger?.error("physicsWorker error", {
        exception: JSON.stringify(error.message),
      });

      physicsWorker.postMessage({
        type: PddPayloadTypes.RemoveEventListener,
      });
      errorCallback?.(
        "physicsWorker encountered an error. Falling back to instant dice.",
      );
      setHasPhysicsWorkerErrored?.(true);
    };

    physicsWorker.addEventListener("error", handleError);
    physicsWorker.addEventListener("message", handlePhysicsMessage);

    return () => {
      physicsWorker.removeEventListener("error", handleError);
      physicsWorker.removeEventListener("message", handlePhysicsMessage);
    };
  }, [
    errorCallback,
    logger,
    userManifest,
    physicsWorker,
    renderedWorker,
    setHasPhysicsWorkerErrored,
    setIsPhysicsWorkerReady,
    toastCallback,
  ]);
};
